/**
 * Marketing Effectiveness Closed-Loop Analysis — Mock API
 * 多渠道营销效果闭环数据（TikTok/亚马逊/Facebook/Google/Instagram）
 */

// 工具函数：生成日期序列
const generateDates = (days) => {
  const dates = [];
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    dates.push(`${d.getMonth() + 1}/${d.getDate()}`);
  }
  return dates;
};

// 工具函数：生成波动数据
const generateTrend = (base, variance, count) => {
  const data = [];
  let current = base;
  for (let i = 0; i < count; i++) {
    current += (Math.random() - 0.45) * variance;
    data.push(Math.round(Math.max(current, base * 0.5)));
  }
  return data;
};

/* ============================================================
   1. 概览数据 — 顶部统计卡片
   ============================================================ */
export const getOverview = (days = 30) => {
  const delay = 150 + Math.random() * 200;
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve({
        totalRevenue: 285600.00,     // ROAS总收入
        totalSpend: 98500.00,        // 总支出
        roasRatio: 2.90,            // ROAS倍数
        conversionRate: 3.82,       // 转化率 (%)
        trends: {
          revenue: { value: 18.5, direction: 'up' },
          spend: { value: 8.2, direction: 'up' },
          roas: { value: 12.3, direction: 'up' },
          conversion: { value: 5.6, direction: 'up' },
        },
        period: days,
      });
    }, delay);
  });
};

/* ============================================================
   2. 渠道数据 — 各渠道效果明细
   ============================================================ */
export const getChannelData = (days = 30) => {
  const delay = 150 + Math.random() * 200;
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve([
        {
          channel: 'TikTok',
          impressions: 3200000,
          clicks: 128000,
          ctr: 4.00,
          cpc: 0.62,
          spend: 79360,
          revenue: 333312,
          roas: 4.20,
          conversions: 5120,
          color: '#00D4FF',
          trend: 'up',
          trendValue: 22.5,
        },
        {
          channel: '亚马逊',
          impressions: 1850000,
          clicks: 55500,
          ctr: 3.00,
          cpc: 0.89,
          spend: 49395,
          revenue: 103730,
          roas: 2.10,
          conversions: 2220,
          color: '#FF9900',
          trend: 'down',
          trendValue: -5.3,
        },
        {
          channel: 'Facebook',
          impressions: 2100000,
          clicks: 84000,
          ctr: 4.00,
          cpc: 0.75,
          spend: 63000,
          revenue: 176400,
          roas: 2.80,
          conversions: 3360,
          color: '#1877F2',
          trend: 'up',
          trendValue: 8.7,
        },
        {
          channel: 'Google',
          impressions: 1600000,
          clicks: 64000,
          ctr: 4.00,
          cpc: 0.95,
          spend: 60800,
          revenue: 164160,
          roas: 2.70,
          conversions: 2560,
          color: '#4285F4',
          trend: 'up',
          trendValue: 6.2,
        },
        {
          channel: 'Instagram',
          impressions: 2400000,
          clicks: 72000,
          ctr: 3.00,
          cpc: 0.68,
          spend: 48960,
          revenue: 137088,
          roas: 2.80,
          conversions: 2880,
          color: '#F472B6',
          trend: 'up',
          trendValue: 15.4,
        },
      ]);
    }, delay);
  });
};

/* ============================================================
   3. 转化漏斗数据 — 曝光→点击→加购→购买→复购
   ============================================================ */
export const getFunnelData = () => {
  const delay = 150 + Math.random() * 200;
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve({
        stages: ['曝光', '点击', '加购', '购买', '复购'],
        values: [11500000, 460000, 92000, 43700, 13110],
        rates: [100, 4.0, 20.0, 47.5, 30.0], // 每步转化率
      });
    }, delay);
  });
};

/* ============================================================
   4. ROAS归因数据 — 各渠道收入贡献占比
   ============================================================ */
export const getAttribution = () => {
  const delay = 150 + Math.random() * 200;
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve([
        { value: 333312, name: 'TikTok', roas: 4.20 },
        { value: 137088, name: 'Instagram', roas: 2.80 },
        { value: 176400, name: 'Facebook', roas: 2.80 },
        { value: 164160, name: 'Google', roas: 2.70 },
        { value: 103730, name: '亚马逊', roas: 2.10 },
      ]);
    }, delay);
  });
};

/* ============================================================
   5. 时间序列数据 — 30天支出+收入趋势
   ============================================================ */
export const getTrend = (days = 30) => {
  const delay = 200 + Math.random() * 200;
  return new Promise((resolve) => {
    setTimeout(() => {
      const dates = generateDates(days);
      const spendBase = Math.round(98500 / days);
      const revenueBase = Math.round(285600 / days);
      resolve({
        dates,
        spend: generateTrend(spendBase, spendBase * 0.3, days),
        revenue: generateTrend(revenueBase, revenueBase * 0.35, days),
        // 各渠道收入趋势
        channelRevenue: {
          TikTok: generateTrend(Math.round(333312 / days), 2000, days),
          Instagram: generateTrend(Math.round(137088 / days), 800, days),
          Facebook: generateTrend(Math.round(176400 / days), 1000, days),
          Google: generateTrend(Math.round(164160 / days), 900, days),
          亚马逊: generateTrend(Math.round(103730 / days), 600, days),
        },
      });
    }, delay);
  });
};
